<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import AppButton from '@/Components/AppButton.vue'
import BootstrapModal from '@/Components/BootstrapModal.vue'
import FeaturedCardIcon from '@/Components/Links/FeaturedCardIcon.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

function emitLockedClick(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
  emit('click', { link: props.link, mode: (props.link?.options?.twitch?.display_mode || 'channel') })
}

const layout = computed(() => props.link?.options?.layout || 'classic')
const isFeatured = computed(() => layout.value === 'featured')
const isClassic  = computed(() => layout.value === 'classic')

const displayMode = computed(() => props.link?.options?.twitch?.display_mode || 'channel')
const isChat    = computed(() => displayMode.value === 'chat')
const isBoth    = computed(() => displayMode.value === 'both')
const isLink    = computed(() => displayMode.value === 'link')
const isChannel = computed(() => displayMode.value === 'channel')
const coverImageUrl = computed(() =>
  buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file)
)

// Channel meta derived from props (no external fetch). Allows backend to optionally populate.
const username = computed(() => {
  const url = props.link?.url
  if (!url) return null
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([^\/?]+)/i)
  return match && match[1] ? match[1] : null
})
const channelName = computed(() => props.link?.options?.twitch?.channel_name || props.link?.title || username.value || 'Twitch')
const avatarUrl = computed(() => props.link?.options?.twitch?.avatar_url || null)
// liveStatus is tri-state: true (live), false (offline), undefined/null (unknown)
const liveStatus = computed(() => props.link?.options?.twitch?.is_live)
const viewers = computed(() => {
  const v = props.link?.options?.twitch?.viewers
  return typeof v === 'number' && v >= 0 ? v : null
})
const streamTitle = computed(() => props.link?.options?.twitch?.stream_title || null)

// Detect desktop to decide between modal vs slide-down for chat mode in classic layout
const isDesktop = ref(true)
let __twResize = null
onMounted(() => {
  try { isDesktop.value = window.innerWidth >= 992 } catch (e) {}
  const handler = () => { isDesktop.value = window.innerWidth >= 992 }
  __twResize = handler
  if (typeof window !== 'undefined') window.addEventListener('resize', handler)
})
onUnmounted(() => {
  if (typeof window !== 'undefined' && __twResize) window.removeEventListener('resize', __twResize)
})
const desktopChat = computed(() => isChat.value && isDesktop.value)

// Fullscreen modal handling for classic + chat
const modalRef = ref(null)
const modalId = computed(() => {
  const idPart = props.link?.id ?? (props.link?.url ? props.link.url.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24) : 'default')
  return `twitch-chat-modal-${idPart}`
})
function openTwitchModal(e){
  e?.stopPropagation?.()
  // If we cannot build chat embed URL, fallback to popout/new tab
  if (!chatEmbedUrl?.value) { return openChatPopout(e) }
  if (modalRef.value) modalRef.value.showModal(modalId.value)
}

function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }
function onClick(e){ emitLockedClick(e) }

function openUrl(e){
  e?.stopPropagation?.()
  const url = props.link?.url
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function getTwitchUsername(link){
  const url = link?.url
  if (!url) return null
  const match = url.match(/(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([^\/?]+)/i)
  return match && match[1] ? match[1] : null
}

function openChatPopout(e){
  e?.stopPropagation?.()
  const username = getTwitchUsername(props.link)
  if (!username) { openUrl(e); return }
  const popUrl = `https://www.twitch.tv/popout/${username}/chat?popout=`
  window.open(popUrl, '_blank', 'noopener,noreferrer')
}

function generateTwitchEmbedUrl(link) {
  if (!link?.url) return null
  const username = getTwitchUsername(link)
  if (!username) return null
  const autoplay = link.options?.twitch?.autoplay ? 'true' : 'false'
  const muted = link.options?.twitch?.mute ? 'true' : 'false'
  const parent = typeof window !== 'undefined' ? window.location.hostname : ''
  const mode = link.options?.twitch?.display_mode
  if (mode === 'chat') {
    // Chat-only embed URL (used when the card is explicitly in chat mode)
    return `https://www.twitch.tv/embed/${username}/chat?parent=${parent}&autoplay=${autoplay}&muted=${muted}`
  }
  if (mode === 'both') {
    // For 'both', we now show video only in the embed and provide a separate Chat button under it
    return `https://embed.twitch.tv?channel=${username}&layout=video&parent=${parent}&autoplay=${autoplay}&muted=${muted}&width=100%&height=100%`
  }
  // Default to channel video
  return `https://embed.twitch.tv?channel=${username}&layout=video&parent=${parent}&autoplay=${autoplay}&muted=${muted}&width=100%&height=100%`
}

const embedUrl = computed(() => displayMode.value !== 'link' ? generateTwitchEmbedUrl(props.link) : null)

// Dedicated chat embed URL (used for popout/modal when needed)
const chatEmbedUrl = computed(() => {
  if (!props.link?.url) return null
  const username = getTwitchUsername(props.link)
  if (!username) return null
  const parent = typeof window !== 'undefined' ? window.location.hostname : ''
  return `https://www.twitch.tv/embed/${username}/chat?parent=${parent}`
})

// Classic layout expansion state for channel mode
const playing = ref(false)
function openTarget(e){
  // Link mode: open external URL
  if (isLink.value) { openUrl(e); return }
  // Classic + chat:
  if (isClassic.value && isChat.value) {
    if (isDesktop.value) { playing.value = !playing.value; return }
    else { openTwitchModal(e); return }
  }
  // Other playable modes: toggle slide-down embed
  if (isChannel.value || isBoth.value) { playing.value = !playing.value; return }
  // Fallback: delegate to parent
  onClick()
}

const twitchIcon = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none"><path fill="%23ffffff" d="M13 7.5l-2 2H9l-1.75 1.75V9.5H5V2h8v5.5z"/><g fill="%239146FF"><path d="M4.5 1L2 3.5v9h3V15l2.5-2.5h2L14 8V1H4.5zM13 7.5l-2 2H9l-1.75 1.75V9.5H5V2h8v5.5z"/><path d="M11.5 3.75h-1v3h1v-3zM8.75 3.75h-1v3h1v-3z"/></g></svg>'
</script>

<template>
  <!-- FEATURED layout: overlay box like YouTube; reduce height for chat/both -->
  <GeneralEmbedButton v-if="isFeatured"
                      class="twitch-card"
                      :link="link"
                      :appearance="appearance"
                      :buttonStyle="buttonStyle"
                      :DoBucket="DoBucket"
                      :DoEndpoint="DoEndpoint"
                      :layout="layout"
                      @click="emitLockedClick">

    <!-- Header row for featured + link mode: left Twitch icon, centered title, right share (unified with SoundCloud) -->
    <div v-if="isLink" class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <!-- Left Twitch Icon -->
      <FeaturedCardIcon :link="link" platform="twitch" :fallbackIcon="twitchIcon" :DoBucket="DoBucket" :DoEndpoint="DoEndpoint" />
      <!-- Center Title between icons -->
      <CardTitle :title="link.title || 'Twitch'" layout="classic" align="center" :max="100" class="flex-grow-1" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
    </div>

    <!-- Header row for featured + chat mode: show avatar + name + live/offline + optional title -->
    <div v-if="isChat" class="d-flex align-items-center mb-2 tw-channel-header">
      <img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" class="tw-avatar" />
      <img v-else :src="twitchIcon" alt="Twitch" class="tw-avatar" />
      <div class="tw-header-text flex-grow-1">
        <div class="tw-header-line">
          <span class="tw-channel-name">{{ channelName }}</span>
          <span v-if="liveStatus === true" class="tw-badge live">LIVE<span v-if="viewers !== null" class="tw-viewers"> • {{ viewers.toLocaleString() }} watching</span></span>
          <span v-else-if="liveStatus === false" class="tw-badge offline">Offline</span>
        </div>
        <div v-if="streamTitle" class="tw-stream-title" :title="streamTitle">{{ streamTitle }}</div>
      </div>
      <span class="d-flex align-items-center" aria-label="Share">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
    </div>

    <div class="tw-box" :class="[appearance.buttons]">
      <template v-if="embedUrl">
          <!-- Fullscreen (popout) when Featured + Chat: show on both desktop and mobile -->
          <button v-if="isChat" class="fullscreen-btn left" @click.stop="openChatPopout" aria-label="Open chat in new window">
            <!-- Fullscreen icon -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H7v5zm10 7h-3v2h5v-5h-2v3zM16 5h-2v2h3v3h2V5h-3z"/>
            </svg>
          </button>
          <!-- Open in new tab when Featured + Chat: place next to fullscreen on the left -->
          <button v-if="isChat" class="open-tab-left" @click.stop="openUrl" aria-label="Open in new tab">
            <!-- External link (open in new tab) icon -->
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
              <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
            </svg>
          </button>
          <!-- Open profile (featured + channel) -->
          <!-- Moved open-profile action into overlay header near title to prevent being hidden by tw-overlay-header -->
          <button v-if="false" class="open-profile-btn" @click.stop="openUrl" aria-label="Open Twitch profile" :style="{ left: '8px', right: 'auto' }">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
              <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
            </svg>
          </button>
        <iframe :src="embedUrl" class="tw-abs" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
        <!-- Overlay channel header for featured layout (channel/both) -->
        <div v-if="!isChat" class="tw-abs tw-overlay-header">
          <div class="tw-channel-header inline">
            <img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" class="tw-avatar" />
            <img v-else :src="twitchIcon" alt="Twitch" class="tw-avatar" />
            <div class="tw-header-text">
              <div class="tw-header-line">
                <span class="tw-channel-name">{{ channelName }}</span>
                <span v-if="liveStatus === true" class="tw-badge live">LIVE<span v-if="viewers !== null" class="tw-viewers"> • {{ viewers.toLocaleString() }} watching</span></span>
                <span v-else-if="liveStatus === false" class="tw-badge offline">Offline</span>
                <!-- Inline open in new tab near title to avoid being hidden by overlay header (desktop only) -->
                <button v-if="isDesktop" class="header-open-tab" @click.stop="openUrl" aria-label="Open in new tab">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
                    <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
                  </svg>
                </button>
              </div>
              <div v-if="streamTitle" class="tw-stream-title" :title="streamTitle">{{ streamTitle }}</div>
            </div>
          </div>
        </div>
        <!-- Mobile: Open in new tab button under share icon when overlay header is visible -->
        <button v-if="!isDesktop && !isChat" class="open-tab-under-share" @click.stop="openUrl" aria-label="Open in new tab">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
            <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
          </svg>
        </button>
        <div v-if="false" class="video-badge">
          <div class="title-row">
            <span class="brand-chip">
                <svg style="width:1.5rem; height:1.5rem; object-fit:contain;"  viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill="#ffffff" d="M13 7.5l-2 2H9l-1.75 1.75V9.5H5V2h8v5.5z"></path> <g fill="#9146FF"> <path d="M4.5 1L2 3.5v9h3V15l2.5-2.5h2L14 8V1H4.5zM13 7.5l-2 2H9l-1.75 1.75V9.5H5V2h8v5.5z"></path> <path d="M11.5 3.75h-1v3h1v-3zM8.75 3.75h-1v3h1v-3z"></path> </g> </g></svg>

            </span>
            <CardTitle :title="link.title || 'Twitch'" layout="featured" align="left" />
          </div>
        </div>
        <button class="share-top-btn" @click.prevent.stop="onShare" aria-label="Share">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" fill="currentColor"
                  d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
          </svg>
        </button>
      </template>
      <template v-else>
        <!-- Link mode (no embed): show file image as background clickable area -->
        <div
          class="tw-abs w-100 h-100"
          :style="{
            backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer'
          }"
          @click.stop="openUrl"
        ></div>
      </template>
    </div>

    <!-- Under-video Chat button for featured layout in 'both' mode -->
    <div v-if="isBoth" class="mt-2">
      <AppButton ignore-plan-restriction :secondary="true" width="100%" @click.stop="isDesktop ? openChatPopout($event) : openTwitchModal($event)" :fontWeight="appearance.font_weight" :custom-class="appearance.buttons" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color" >
        Open Chat
      </AppButton>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC layout: delegate click to parent (opens modal or navigates) -->
  <GeneralButton v-else
                 class="twitch-card"
                 :link="link"
                 :appearance="appearance"
                 :buttonStyle="buttonStyle"
                 :DoBucket="DoBucket"
                 :DoEndpoint="DoEndpoint"
                 :layout="layout"
                 :hideLeftImage="(isChannel || isChat || isBoth) && playing"
                 @click="emitLockedClick">
    <template #title>
      <CardTitle :title="link.title || 'Twitch'" layout="classic" align="center" :max="100" />
    </template>
    <template #actions>
      <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
      </svg>
    </template>

    <transition name="slide-fade">
      <div v-if="((isChannel || isBoth) || desktopChat) && playing" class="classic-embed mt-2 w-100">
        <div class="tw-box" :class="[appearance.buttons, desktopChat ? 'chat-tall' : '']">

          <!-- Fullscreen (popout) button to open chat in a new window (desktop classic + chat slide-down) -->
          <button v-if="desktopChat" class="fullscreen-btn left" @click.stop="openChatPopout" aria-label="Open chat in new window">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H7v5zm10 7h-3v2h5v-5h-2v3zM16 5h-2v2h3v3h2V5h-3z"/>
            </svg>
          </button>

          <!-- Open in new tab for desktop chat: place next to fullscreen on the left -->
          <button v-if="desktopChat" class="open-tab-left" @click.stop="openUrl" aria-label="Open in new tab">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
              <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
            </svg>
          </button>

          <!-- Open profile in new tab (classic slide-down for channel/both) - keep on the right -->
          <!-- Moved open-profile action into overlay header near title to prevent being hidden by tw-overlay-header -->
          <button v-else-if="(isChannel || isBoth) && false" class="open-profile-btn" @click.stop="openUrl" aria-label="Open Twitch profile">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
              <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
            </svg>
          </button>


          <iframe :src="embedUrl" class="tw-abs" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
          <!-- Overlay channel header for classic slide-down (channel/both/chat) -->
          <div class="tw-abs tw-overlay-header">
            <div class="tw-channel-header inline">
              <img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" class="tw-avatar" />
              <img v-else :src="twitchIcon" alt="Twitch" class="tw-avatar" />
              <div class="tw-header-text">
                <div class="tw-header-line">
                  <span class="tw-channel-name">{{ channelName }}</span>
                  <span v-if="liveStatus === true" class="tw-badge live">LIVE<span v-if="viewers !== null" class="tw-viewers"> • {{ viewers.toLocaleString() }} watching</span></span>
                  <span v-else-if="liveStatus === false" class="tw-badge offline">Offline</span>
                  <!-- Inline open in new tab near title to avoid being hidden by overlay header -->
                  <button v-if="isDesktop" class="header-open-tab" @click.stop="openUrl" aria-label="Open in new tab">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill="currentColor" d="M14 3h7v7h-2V6.414l-9.293 9.293-1.414-1.414L17.586 5H14V3z"/>
                      <path fill="currentColor" d="M19 19H5V5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z"/>
                    </svg>
                  </button>
                </div>
                <div v-if="streamTitle" class="tw-stream-title" :title="streamTitle">{{ streamTitle }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Under-video Chat button for classic layout in 'both' mode -->
        <div v-if="isBoth" class="mt-2">
          <AppButton ignore-plan-restriction :secondary="true" width="100%" @click.stop="isDesktop ? openChatPopout($event) : openTwitchModal($event)" :fontWeight="appearance.font_weight" :custom-class="appearance.buttons" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color" >
            Open Chat
          </AppButton>
        </div>
      </div>
    </transition>
  </GeneralButton>

  <!-- Fullscreen Modal for chat on mobile (classic chat or both in classic/featured) -->
  <teleport to="body">
    <BootstrapModal
      v-if="!isDesktop && ((isClassic && (isChat || isBoth)) || (isFeatured && (isChat || isBoth)))"
      :modalId="modalId"
      :title="link.title || 'Twitch Chat'"
      modalSize="fullscreen"
      ref="modalRef"
    >
      <template #body>
        <div style="width:100%; height: calc(100vh - 140px); position: relative; background:#000;">
          <iframe v-if="chatEmbedUrl" :src="chatEmbedUrl" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
        </div>
      </template>
    </BootstrapModal>
  </teleport>
</template>

<style scoped>
.twitch-card :deep(iframe){ border: 0; }
.tw-box{ position:relative; background:#000; overflow:hidden; aspect-ratio:16/9; }
.tw-box.short{ aspect-ratio:16/6; }
.tw-abs{ position:absolute; inset:0; width:100%; height:100%; }
.video-badge{
  position: absolute !important;
  bottom: 10px !important;
  left: 10px !important;
  display: inline-block;
  width: auto !important;
  max-width: calc(100% - 60px);
  padding: 6px 10px !important;
  background: rgba(0,0,0,0.6);
  color: #fff;
  font-size: 14px;
  line-height: 1.3;
  border-radius: .5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 2;
}
.title-row{ display: inline-flex; align-items: center; gap: 8px; }
.brand-chip{ width: 40px; height: 40px; opacity: 0.85; display: inline-grid; place-items: center; }
.title-icon{ width: 20px; height: 20px; object-fit: contain; }
.share{ position:absolute; right:16px; bottom:10px; width:24px; height:24px; display:grid; place-items:center; border-radius:50%; color:#fff; z-index:3; border:0; background:transparent; }
/* Featured header row spacing and truncation (chat mode) */
.tw-header-row { padding: 0 8px; gap: 8px; }
.tw-header-row .left-icon { margin-right: 8px; }
.tw-header-row .share-wrapper { margin-left: 8px; }
.tw-header-row .title { flex: 1; text-align: center; padding: 0 8px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* Inline header open-tab button placed near title in overlay header */
.header-open-tab{ margin-left:6px; width:24px; height:24px; display:inline-flex; align-items:center; justify-content:center; border:0; background:transparent; color:#fff; cursor:pointer; padding:0; border-radius:4px; }
.header-open-tab:hover{ background:rgba(255,255,255,0.15); }
/* Classic slide-down area */
.classic-embed .tw-box{ position:relative; aspect-ratio:16/9; background:#000; overflow:hidden; }
/* Taller box for desktop chat-only slide-down */
.tw-box.chat-tall{ aspect-ratio:auto; height:600px; }
/* Taller box for desktop classic chat-only slide-down */
.classic-embed .tw-box.chat-tall{ aspect-ratio:auto; height:600px; }
/* Taller box for featured both (video with chat) */
.tw-box.both-tall{ aspect-ratio:auto; height:600px; }
/* Slide down transition */
.slide-fade-enter-from,.slide-fade-leave-to{ max-height:0; opacity:0; transform:translateY(-6px); }
.slide-fade-enter-active,.slide-fade-leave-active{ transition:all .25s ease; }
.slide-fade-enter-to,.slide-fade-leave-from{ max-height:1000px; opacity:1; transform:translateY(0); }
/* Close button overlay */
.close-btn{ position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.close-btn:hover{ background:rgba(0,0,0,.6); }
/* Popout (open in new tab) button */
 .popout-btn{ position:absolute; top:8px; left:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.popout-btn:hover{ background:rgba(0,0,0,.6); }
/* Open profile button (top-right overlay) */
.open-profile-btn{ position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.open-profile-btn:hover{ background:rgba(0,0,0,.6); }
/* Fullscreen button (top-right overlay on mobile) */
.fullscreen-btn{ position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.fullscreen-btn:hover{ background:rgba(0,0,0,.6); }
/* Modifier to place fullscreen button on the left (used for featured + chat on mobile) */
.fullscreen-btn.left{ left:8px; right:auto; }
/* Header open-in-new-tab button (featured + chat) */
.open-tab-btn{ width:24px; height:24px; display:flex; align-items:center; justify-content:center; border:0; background:transparent; padding:0; cursor:pointer; }
/* Left-side open-in-new-tab overlay button (placed near fullscreen) */
.open-tab-left{ position:absolute; top:8px; left:44px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.open-tab-left:hover{ background:rgba(0,0,0,.6); }
/* Mobile: open-tab button positioned under the share icon (featured overlay header) */
.open-tab-under-share{ position:absolute; top:44px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.open-tab-under-share:hover{ background:rgba(0,0,0,.6); }
/* Top-right share button for featured channel mode */
.share-top-btn{ position:absolute; top:8px; right:8px; width:28px; height:28px; display:grid; place-items:center; border-radius:50%; background:rgba(0,0,0,.45); color:#fff; border:0; cursor:pointer; z-index:4; }
.share-top-btn:hover{ background:rgba(0,0,0,.6); }
.tw-channel-header{ display:flex; align-items:center; gap:10px; padding:6px 8px; max-width:100%; overflow:hidden; }
.tw-channel-header.inline{ background:rgba(0,0,0,0.55); border-radius:8px; }
.tw-avatar{ width:28px; height:28px; border-radius:50%; object-fit:cover; flex:0 0 auto; }
.tw-header-text{ display:flex; flex-direction:column; min-width:0; max-width:100%; }
.tw-header-line{ display:flex; align-items:center; gap:8px; min-width:0; max-width:100%; overflow:hidden; }
.tw-channel-name{ font-weight:700; color:#fff; max-width:100%; flex:1 1 auto; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tw-badge{ font-size:12px; padding:2px 6px; border-radius:999px; line-height:1; white-space:nowrap; }
.tw-badge.live{ background:#e91916; color:#fff; }
.tw-badge.offline{ background:rgba(255,255,255,0.25); color:#fff; }
.tw-viewers{ opacity:0.9; margin-left:2px; }
.tw-stream-title{ color:#fff; font-size:12px; opacity:0.95; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%; }
/* Overlay header container at top-left */
 .tw-overlay-header{ pointer-events:none; padding:8px; display:flex; align-items:flex-start; z-index:3; width:100%; box-sizing:border-box; }
.tw-overlay-header .tw-channel-header{ pointer-events:auto; max-width:100%; }

</style>
